import React from 'react';
import { AlertCircle, X } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onClear?: () => void;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      leftIcon,
      rightIcon,
      onClear,
      disabled,
      className = '',
      id,
      value,
      ...props
    },
    ref
  ) => {
    const inputId = id || (label ? `input-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);
    const hasValue = value !== undefined && value !== '' && value !== null;

    return (
      <div className="w-full space-y-1.5">
        {label && (
          <label htmlFor={inputId} className="block text-xs font-bold text-[#111111] uppercase tracking-wider">
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {leftIcon && (
            <div className="absolute left-3 text-[#5F6368] pointer-events-none shrink-0">{leftIcon}</div>
          )}
          <input
            id={inputId}
            ref={ref}
            value={value}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
            className={`w-full bg-white text-[#111111] text-xs sm:text-sm font-medium rounded-xl border transition-all duration-150 outline-none placeholder:text-[#80868B] disabled:bg-[#F1F3F4] disabled:text-[#9AA0A6] disabled:cursor-not-allowed ${
              leftIcon ? 'pl-9' : 'pl-3.5'
            } ${rightIcon || onClear || error ? 'pr-9' : 'pr-3.5'} py-2.5 ${
              error
                ? 'border-[#D94A5C] focus:ring-2 focus:ring-[#D94A5C]/20'
                : 'border-[#E5E9E9] focus:border-[#1fb4ac] focus:ring-2 focus:ring-[#1fb4ac]/20'
            } ${className}`}
            {...props}
          />
          <div className="absolute right-3 flex items-center gap-1.5 text-[#5F6368]">
            {onClear && hasValue && !disabled && (
              <button
                type="button"
                onClick={onClear}
                aria-label="Clear input"
                className="hover:text-[#111111] p-0.5 rounded transition-colors cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {error ? (
              <AlertCircle className="w-4 h-4 text-[#D94A5C] shrink-0" />
            ) : (
              rightIcon && <span className="shrink-0">{rightIcon}</span>
            )}
          </div>
        </div>
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-[#D94A5C] font-medium flex items-center gap-1">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${inputId}-helper`} className="text-xs text-[#5F6368]">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
